import { useMutation, useQuery, useQueryClient } from 'react-query';
import { queryGet, queryUpdate } from './fetch';
import { AxiosError } from 'axios';
import { DevicesCategory } from './session';

export enum LinkConfigurationStepConfirmation {
  RECORDING_COMPLETION_LINK = 'RECORDING_COMPLETION_LINK',
  RECORDING_COMPLETION_CODE = 'RECORDING_COMPLETION_CODE',
  MATCHING_PARTICIPANT_URL = 'MATCHING_PARTICIPANT_URL',
  MATCHING_PARTICIPANT_ID = 'MATCHING_PARTICIPANT_ID',
}

export enum ModeratedTaskFormat {
  PHYSICAL_MEETING = 'PHYSICAL_MEETING',
  VIDEO_CALL = 'VIDEO_CALL',
  PHONE_CALL = 'PHONE_CALL',
}

export interface ITaskSetting {
  id: string;
  sessionId: string;
}

export enum VideoCallProvider {
  CUSTOM = 'custom',
  MICROSOFT_TEAMS = 'ms_team',
  ZOOM = 'zoom',
  GO_TO_MEETING = 'go_to',
  INTEGRATED = 'integrated',
  GOOGLE_MEET = 'google_meet',
  CRONOFY = '8x8',
  BUZZ = 'buzz',
}

export interface UnmoderatedTaskLink {
  id: string;
  taskSettingId: string;
  studyToolLink: string;
  metadataKey?: string;
  metadataValues?: string[];
}

export interface Attendee {
  id: string;
  email: string;
  accountId?: string;
  taskSettingId: string;
}

export interface AttendeeDto {
  id?: string;
  email: string;
  accountId?: string;
  taskSettingId: string;
}

export interface UnmoderatedTaskSetting extends ITaskSetting {
  estimatedDuration: number;
  finishedCode: string;
  completedCode: string;
  reportedCode: string;
  linkConfigurationStepConfirmation: LinkConfigurationStepConfirmation[];

  studyToolLinks: UnmoderatedTaskLink[];
  studyToolInstructions?: string;
  allowedDevices?: DevicesCategory[];
  areLinksEditable?: boolean;
}

export interface ModeratedTaskSetting extends ITaskSetting {
  format: ModeratedTaskFormat;
  appointmentDuration: number;
  appointmentBuffer: number;
  proposedMeetingInterval: number;
  groupMeeting: boolean;
  numberOfParticipantByMeeting: number;
  bookingDelayInHours: number;
  maxDaysLimit: number;
  displayWeekendOnCalendar: boolean;
  shouldCheckForConflicts: boolean;
  ignoreAvailableEvents: boolean;
  attendees: Attendee[];
  moderatorAccountId: string;
  moderatorCalendarId?: string;
  videoCallProvider?: VideoCallProvider;
  videoCallLink?: string;
  videoCallDetails?: string;
  allowedDevices?: DevicesCategory[];
  address?: string;
  addressDetails?: string;
  phoneVisibilityConsent?: boolean;
  visiblePhoneNumber?: string;
  videoCallProviderId?: string;
}

export interface EventConferencing {
  method: VideoCallProvider;
  link?: string;
}

export interface TdmzEvent {
  id: string;
  start: Date;
  end: Date;
  location?: {
    description: string;
    instructions?: string;
    lat?: string;
    long?: string;
  };

  format: ModeratedTaskFormat;
  instructions?: string;

  conferencing?: EventConferencing;
  sessionId: string;
  videoCallProviderId?: string;
  startSequence: number;
  locale: string;
  title: string;
  description?: string;

  attendees?: string[];
  participants: string[];
}

export type TaskSetting = UnmoderatedTaskSetting | ModeratedTaskSetting;

export const useTaskSetting = <
  T extends TaskSetting = UnmoderatedTaskSetting | ModeratedTaskSetting,
>(
  id?: string,
) => {
  return useQuery<T>(
    ['task-settings', id],
    queryGet(`/v2/task-settings/${id}`),
    {
      enabled: !!id,
    },
  );
};

export const useUnmoderatedTaskSettingByLinkConfig = (id?: string) => {
  return useQuery<UnmoderatedTaskSetting>(
    ['link-configuration', id, 'unmoderated-task-setting'],
    queryGet(`/v2/link-configurations/${id}/unmoderated-task-setting`),
    {
      enabled: !!id,
    },
  );
};

export const useUpdateModeratedTaskSetting = (id?: string) => {
  const queryClient = useQueryClient();
  return useMutation<
    ModeratedTaskSetting,
    AxiosError,
    Partial<ModeratedTaskSetting>
  >(queryUpdate(`/v2/moderated-task-settings/${id}`), {
    onSuccess: (data) => {
      queryClient.setQueryData(['task-settings', id], data);
    },
  });
};

export const useUpdateUnmoderatedTaskSetting = (id?: string) => {
  const queryClient = useQueryClient();
  return useMutation<
    UnmoderatedTaskSetting,
    AxiosError,
    Partial<UnmoderatedTaskSetting>
  >(queryUpdate(`/v2/unmoderated-task-settings/${id}`), {
    onSuccess: (data) => {
      queryClient.setQueryData(['task-settings', id], data);
    },
  });
};

export const useTaskSettingAttendees = (id?: string) => {
  return useQuery<Attendee[]>(
    ['task-settings', id, 'attendees'],
    queryGet(`/v2/task-settings/${id}/attendees`),
    {
      enabled: !!id,
    },
  );
};

export const useTaskSettingEvents = (id?: string) => {
  return useQuery<TdmzEvent[]>(
    ['task-settings', id, 'events'],
    queryGet(`/v2/task-settings/${id}/events`),
    {
      enabled: !!id,
    },
  );
};
